import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AgentConfigManager.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface AgentConfig {
  agent_name: string;
  model: string;
  max_tokens: number;
  max_steps: number;
  prompt_extension: string | null;
  aktiv: boolean;
  aktualisiert_am: string | null;
  provider: string;
  runtime: string;
  base_url: string | null;
  api_key_config_key: string | null;
}

interface ModelInfo {
  id: string;
  display_name: string;
  empfohlen: boolean;
}

interface DirtyConfig {
  model?: string;
  max_tokens?: number;
  max_steps?: number;
  prompt_extension?: string;
  aktiv?: boolean;
  provider?: string;
  runtime?: string;
  base_url?: string;
  api_key_config_key?: string;
}

const PROVIDERS = ['anthropic', 'openai', 'gemini', 'local'];
const RUNTIMES = ['generic', 'sdk'];

interface Props {
  authToken: string;
}

// Tool-Kategorien für Anzeige (READ/WRITE-Badges)
const AGENT_TOOLS: Record<string, { name: string; category: 'READ' | 'EXECUTE' }[]> = {
  'Orchestrator': [
    { name: 'lager_agent_anfragen', category: 'EXECUTE' },
    { name: 'einkauf_agent_anfragen', category: 'EXECUTE' },
    { name: 'verkauf_agent_anfragen', category: 'EXECUTE' },
    { name: 'produktion_agent_anfragen', category: 'EXECUTE' },
    { name: 'reporting_agent_anfragen', category: 'EXECUTE' },
    { name: 'qualitaet_agent_anfragen', category: 'EXECUTE' },
    { name: 'dokumenten_agent_anfragen', category: 'EXECUTE' },
    { name: 'konfiguration_agent_anfragen', category: 'EXECUTE' },
  ],
  'Lager-Agent': [
    { name: 'bestand_abfragen', category: 'READ' },
    { name: 'artikel_suchen', category: 'READ' },
    { name: 'mindestbestaende_pruefen', category: 'READ' },
    { name: 'wareneingang_buchen', category: 'EXECUTE' },
    { name: 'warenausgang_buchen', category: 'EXECUTE' },
    { name: 'umlagerung_buchen', category: 'EXECUTE' },
  ],
  'Einkaufs-Agent': [
    { name: 'lieferanten_suchen', category: 'READ' },
    { name: 'bestellvorschlaege_generieren', category: 'READ' },
    { name: 'letzte_bestellung_pruefen', category: 'READ' },
    { name: 'preisvergleich', category: 'READ' },
    { name: 'bestellung_erstellen', category: 'EXECUTE' },
    { name: 'bestellung_status_aendern', category: 'EXECUTE' },
  ],
  'Verkaufs-Agent': [
    { name: 'auftraege_abfragen', category: 'READ' },
    { name: 'kunden_suchen', category: 'READ' },
    { name: 'preis_kalkulieren', category: 'READ' },
    { name: 'verfuegbarkeit_pruefen', category: 'READ' },
    { name: 'angebot_erstellen', category: 'EXECUTE' },
    { name: 'auftrag_status_aendern', category: 'EXECUTE' },
  ],
  'Produktions-Agent': [
    { name: 'fertigungsauftraege_abfragen', category: 'READ' },
    { name: 'material_verfuegbarkeit_pruefen', category: 'READ' },
    { name: 'kapazitaet_abfragen', category: 'READ' },
    { name: 'fertigungsauftrag_erstellen', category: 'EXECUTE' },
    { name: 'fertigungsauftrag_status_aendern', category: 'EXECUTE' },
    { name: 'arbeitsgang_status_aendern', category: 'EXECUTE' },
  ],
  'Reporting-Agent': [
    { name: 'system_uebersicht', category: 'READ' },
    { name: 'lager_auswertung', category: 'READ' },
    { name: 'verkauf_auswertung', category: 'READ' },
    { name: 'einkauf_auswertung', category: 'READ' },
    { name: 'morgen_briefing', category: 'READ' },
    { name: 'produktion_auswertung', category: 'READ' },
  ],
  'Qualitäts-Agent': [
    { name: 'herkunftsnachweis_pruefen', category: 'READ' },
    { name: 'zertifikate_abfragen', category: 'READ' },
    { name: 'reklamationen_abfragen', category: 'READ' },
    { name: 'pruefprotokoll_erstellen', category: 'EXECUTE' },
    { name: 'charge_status_aendern', category: 'EXECUTE' },
    { name: 'reklamation_erfassen', category: 'EXECUTE' },
  ],
  'Dokumenten-Agent': [
    { name: 'eingangsrechnungen_abfragen', category: 'READ' },
    { name: 'ausgangsrechnungen_abfragen', category: 'READ' },
    { name: 'dokumente_abfragen', category: 'READ' },
    { name: 'rechnungsnummer_pruefen', category: 'READ' },
    { name: 'eingangsrechnung_anlegen', category: 'EXECUTE' },
    { name: 'eingangsrechnung_freigeben', category: 'EXECUTE' },
    { name: 'ausgangsrechnung_erstellen', category: 'EXECUTE' },
  ],
  'KonfigurationsAgent': [
    { name: 'list_custom_fields', category: 'READ' },
    { name: 'add_custom_field', category: 'EXECUTE' },
    { name: 'update_custom_field', category: 'EXECUTE' },
    { name: 'delete_custom_field', category: 'EXECUTE' },
  ],
};

const AgentConfigManager: React.FC<Props> = ({ authToken }) => {
  const [configs, setConfigs] = useState<AgentConfig[]>([]);
  const [allowedModels, setAllowedModels] = useState<ModelInfo[]>([]);
  const [dirty, setDirty] = useState<Record<string, DirtyConfig>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Record<string, { ok: boolean; text: string }>>({});
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  const load = async () => {
    setLoading(true);
    try {
      const [configRes, modelsRes] = await Promise.all([
        axios.get(`${API_URL}/api/agent-config`, { headers }),
        axios.get(`${API_URL}/api/agent-config/models`, { headers }),
      ]);
      setConfigs(configRes.data);
      setAllowedModels(modelsRes.data);
    } catch (e) {
      console.error('Fehler beim Laden der Agent-Konfiguration', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getEffective = (cfg: AgentConfig) => {
    const d = dirty[cfg.agent_name] || {};
    return {
      ...cfg,
      ...d,
      prompt_extension: d.prompt_extension !== undefined ? d.prompt_extension : (cfg.prompt_extension ?? undefined),
    };
  };

  const setField = (agentName: string, field: keyof DirtyConfig, value: string | number | boolean) => {
    setDirty(prev => ({
      ...prev,
      [agentName]: { ...(prev[agentName] || {}), [field]: value },
    }));
  };

  const isDirty = (agentName: string) => !!dirty[agentName] && Object.keys(dirty[agentName]).length > 0;

  const save = async (agentName: string) => {
    const payload = dirty[agentName];
    if (!payload) return;
    setSaving(agentName);
    try {
      await axios.put(`${API_URL}/api/agent-config/${encodeURIComponent(agentName)}`, payload, { headers });
      setFeedback(prev => ({ ...prev, [agentName]: { ok: true, text: 'Gespeichert' } }));
      setDirty(prev => {
        const next = { ...prev };
        delete next[agentName];
        return next;
      });
      await load();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Fehler';
      setFeedback(prev => ({ ...prev, [agentName]: { ok: false, text: msg } }));
    } finally {
      setSaving(null);
      setTimeout(() => setFeedback(prev => { const n = {...prev}; delete n[agentName]; return n; }), 3000);
    }
  };

  if (loading) {
    return <div className="agent-config-loading">Lade Agent-Konfigurationen...</div>;
  }

  return (
    <div className="agent-config-manager">
      <h3>Agent-Konfiguration</h3>
      <p className="agent-config-desc">
        Passe Modell, Token-Limits und Prompt-Extensions pro Agent an.
        Änderungen gelten für neue Konversationen.
      </p>

      {configs.map(cfg => {
        const eff = getEffective(cfg);
        const tools = AGENT_TOOLS[cfg.agent_name] || [];
        const isExpanded = expandedAgent === cfg.agent_name;

        return (
          <div key={cfg.agent_name} className={`agent-card ${!eff.aktiv ? 'agent-card--inactive' : ''}`}>
            <div className="agent-card__header" onClick={() => setExpandedAgent(isExpanded ? null : cfg.agent_name)}>
              <div className="agent-card__title">
                <span className="agent-card__name">{cfg.agent_name}</span>
                {!eff.aktiv && <span className="badge badge--inactive">Inaktiv</span>}
                {isDirty(cfg.agent_name) && <span className="badge badge--dirty">Ungespeichert</span>}
              </div>
              <div className="agent-card__summary">
                <span>{eff.provider}{eff.runtime === 'sdk' ? ' · sdk' : ''}</span>
                <span>{eff.model}</span>
                <span>{eff.max_tokens} Tokens</span>
                <span>{eff.max_steps} Schritte</span>
              </div>
              <span className="agent-card__toggle">{isExpanded ? '▲' : '▼'}</span>
            </div>

            {isExpanded && (
              <div className="agent-card__body">
                <div className="agent-config-grid">
                  {/* Provider */}
                  <div className="agent-config-field">
                    <label>Provider</label>
                    <select
                      value={eff.provider}
                      onChange={e => setField(cfg.agent_name, 'provider', e.target.value)}
                    >
                      {PROVIDERS.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  {/* Runtime */}
                  <div className="agent-config-field">
                    <label>Runtime</label>
                    <select
                      value={eff.runtime}
                      onChange={e => setField(cfg.agent_name, 'runtime', e.target.value)}
                    >
                      {RUNTIMES.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>

                  {/* Modell */}
                  <div className="agent-config-field">
                    <label>Modell</label>
                    {eff.provider === 'anthropic' ? (
                      <select
                        value={eff.model}
                        onChange={e => setField(cfg.agent_name, 'model', e.target.value)}
                      >
                        {(() => {
                          const empfohlen = allowedModels.filter(m => m.empfohlen);
                          const weitere = allowedModels.filter(m => !m.empfohlen);
                          const istBekannt = allowedModels.some(m => m.id === eff.model);
                          return (
                            <>
                              {empfohlen.length > 0 && (
                                <optgroup label="Empfohlen">
                                  {empfohlen.map(m => (
                                    <option key={m.id} value={m.id} title={m.id}>
                                      {m.display_name} ({m.id})
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                              {weitere.length > 0 && (
                                <optgroup label="Weitere Modelle">
                                  {weitere.map(m => (
                                    <option key={m.id} value={m.id} title={m.id}>
                                      {m.display_name} ({m.id})
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                              {!istBekannt && eff.model && (
                                <option value={eff.model}>{`${eff.model} (nicht mehr verfügbar)`}</option>
                              )}
                            </>
                          );
                        })()}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder="z.B. gpt-4o, llama3.1…"
                        value={eff.model}
                        onChange={e => setField(cfg.agent_name, 'model', e.target.value)}
                      />
                    )}
                  </div>

                  {/* max_tokens */}
                  <div className="agent-config-field">
                    <label>Max. Tokens</label>
                    <input
                      type="number"
                      min={512}
                      max={32768}
                      step={512}
                      value={eff.max_tokens}
                      onChange={e => {
                        const parsed = parseInt(e.target.value, 10);
                        if (!isNaN(parsed)) setField(cfg.agent_name, 'max_tokens', parsed);
                      }}
                    />
                  </div>

                  {/* max_steps */}
                  <div className="agent-config-field">
                    <label>Max. Schritte</label>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={eff.max_steps}
                      onChange={e => {
                        const parsed = parseInt(e.target.value, 10);
                        if (!isNaN(parsed)) setField(cfg.agent_name, 'max_steps', parsed);
                      }}
                    />
                  </div>

                  {/* Aktiv */}
                  <div className="agent-config-field agent-config-field--aktiv">
                    <label title="Deaktiviert nur diese Konfiguration, nicht den Agent selbst">
                      Konfiguration aktiv
                      <span className="agent-config-field__hint"> (deaktiviert nur diese Konfiguration, nicht den Agent selbst)</span>
                    </label>
                    <input
                      type="checkbox"
                      checked={eff.aktiv}
                      onChange={e => setField(cfg.agent_name, 'aktiv', e.target.checked)}
                    />
                  </div>
                </div>

                {/* Base-URL (für openai-kompatible/lokale Endpunkte) */}
                {eff.provider !== 'anthropic' && (
                  <div className="agent-config-field agent-config-field--full">
                    <label>Base-URL (optional, für lokale/OpenAI-kompatible Endpunkte)</label>
                    <input
                      type="text"
                      placeholder="z.B. http://localhost:11434/v1"
                      value={eff.base_url || ''}
                      onChange={e => setField(cfg.agent_name, 'base_url', e.target.value)}
                    />
                  </div>
                )}

                {/* API-Key-Config-Key (Indirektion auf hinterlegten Schlüssel) */}
                <div className="agent-config-field agent-config-field--full">
                  <label>API-Key-Config-Key (optional)
                    <span className="agent-config-field__hint"> — Name des Konfigurations-Schlüssels mit dem API-Key; leer = Provider-Default</span>
                  </label>
                  <input
                    type="text"
                    placeholder="z.B. OPENAI_API_KEY"
                    value={eff.api_key_config_key || ''}
                    onChange={e => setField(cfg.agent_name, 'api_key_config_key', e.target.value)}
                  />
                </div>

                {/* Prompt-Extension */}
                <div className="agent-config-field agent-config-field--full">
                  <label>Prompt-Extension (Admin-Anweisungen)</label>
                  <textarea
                    rows={3}
                    placeholder="Zusätzliche Anweisungen für diesen Agenten (optional)..."
                    value={eff.prompt_extension || ''}
                    onChange={e => setField(cfg.agent_name, 'prompt_extension', e.target.value)}
                  />
                </div>

                {/* Tool-Liste */}
                {tools.length > 0 && (
                  <div className="agent-tools">
                    <h4>Tools</h4>
                    <div className="agent-tools__list">
                      {tools.map(t => (
                        <span
                          key={t.name}
                          className={`tool-badge tool-badge--${t.category.toLowerCase()}`}
                          title={t.category}
                        >
                          {t.name}
                          <span className="tool-badge__cat">{t.category === 'READ' ? 'R' : 'W'}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Speichern */}
                <div className="agent-card__actions">
                  {cfg.aktualisiert_am && (
                    <span className="agent-card__updated">
                      Zuletzt geändert: {new Date(cfg.aktualisiert_am).toLocaleString('de-DE')}
                    </span>
                  )}
                  {feedback[cfg.agent_name] && (
                    <span className={`agent-feedback ${feedback[cfg.agent_name].ok ? 'agent-feedback--ok' : 'agent-feedback--err'}`}>
                      {feedback[cfg.agent_name].text}
                    </span>
                  )}
                  <button
                    className="btn-save"
                    disabled={!isDirty(cfg.agent_name) || saving === cfg.agent_name}
                    onClick={() => save(cfg.agent_name)}
                  >
                    {saving === cfg.agent_name ? 'Speichere...' : 'Speichern'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AgentConfigManager;
